/**
 * Corner detection + L-corner-cabinet emission for multi-segment runs.
 * Pure. Reuses the kitchen-assembly L builder; we only decide WHERE the
 * corner is and which leg is wallA. See
 * claude-docs/2026-06-13-manual-cabinet-runs-design.md §3.
 */

const {
  getWallAxes,
  wallParamToSurfacePoint,
} = require('../kitchen-assembly/wallMath');
const {
  buildCornerCabinet,
  UPPER_CORNER_BOX,
} = require('../kitchen-assembly/buildCornerCabinet');
const {
  buildSingleSidedCornerCabinet,
} = require('../kitchen-assembly/buildSingleSidedCornerCabinet');
const {
  buildCornerFillCountertop,
} = require('../kitchen-assembly/buildCornerFillCountertop');
const {
  runStopDistance,
  isSquareCornerCos,
} = require('../kitchen-assembly/cornerDetection');
const { CORNER_CABINET_FOOTPRINT } = require('../kitchen-assembly/constants');
const { cornerBoxFor, segmentCornerDepth } = require('./runDepth');
const { buildPolygonFiller } = require('./polygonFiller');
const { profileOf, runBodyHeight } = require('./profiles');

const {
  JOIN_EPS: COINCIDENT_EPS, // 1 mm — shared-vertex tolerance
  isFillableTurn,
  isRightTurn,
  sameCabinetSide,
  turnDeg,
} = require('./bendGeometry');

// A junction only TURNS if the two legs differ in direction by more than this.
// cos(15°) — below that the segments are collinear enough that the run reads as
// straight, and treating the join as a corner would be actively wrong: it trims
// a 36" corner footprint off BOTH legs and drops an L-cabinet in the middle of
// what is visually one continuous counter. Real walls are routinely captured as
// several nearly-collinear segments (RoomPlan splits one physical wall; a scan
// run therefore spans them), so this is the common case, not an edge case.
const COLLINEAR_COS = Math.cos((15 * Math.PI) / 180);

function sharedCornerPoint(segA, segB) {
  const pA = wallParamToSurfacePoint(segA.wall, segA.endParam);
  const pB = wallParamToSurfacePoint(segB.wall, segB.startParam);
  if (!pA || !pB) return null;
  if (Math.hypot(pA.x - pB.x, pA.z - pB.z) > COINCIDENT_EPS) return null;
  return pA;
}

/** A segment's traversal direction in world space, start → end. */
function segmentDirection(seg) {
  const pStart = wallParamToSurfacePoint(seg.wall, seg.startParam);
  const pEnd = wallParamToSurfacePoint(seg.wall, seg.endParam);
  if (!pStart || !pEnd) return null;
  if (Math.hypot(pEnd.x - pStart.x, pEnd.z - pStart.z) <= COINCIDENT_EPS) {
    return null; // degenerate segment — no direction to compare
  }
  return unitFrom(pStart, pEnd);
}

/**
 * True when two consecutive segments turn a corner: different walls, meeting at
 * a shared vertex, AND actually changing direction there.
 *
 * The direction test is load-bearing. Without it any two segments that merely
 * touch count as a corner, so a run spanning two collinear captured segments of
 * ONE physical wall gets a 36" corner reservation and an L-cabinet dropped into
 * the middle of a straight counter.
 */
function isCorner(segA, segB) {
  // Free-standing segments (peninsula/island) have no backing wall, so they
  // can't form a wall corner with anything — guard before dereferencing.
  if (!segA.wall || !segB.wall) return false;
  if (segA.wall.id === segB.wall.id) return false;
  if (sharedCornerPoint(segA, segB) == null) return false;
  const dA = segmentDirection(segA);
  const dB = segmentDirection(segB);
  if (!dA || !dB) return false;
  // A DRAWN island leg (`bentRunFill.js`) bends where the user drew it: any
  // turn the draw tool did not straighten (5°, its snap) is a corner, and one
  // sharper than 90° is none — the legs are filled apart and the run warns
  // (angled-runs design 2026-11-03). Every other joint keeps the 15° rule,
  // which exists for RoomPlan splitting one wall into near-collinear pieces.
  // Nor is a joint where the cabinets switch sides of the line (a wall leg
  // whose room side is the other side): the legs simply meet there.
  if (segA.wall.drawnFreeLeg || segB.wall.drawnFreeLeg) {
    return isFillableTurn(turnDeg(dA, dB)) && sameCabinetSide(segA, segB);
  }
  return dot(dA, dB) < COLLINEAR_COS;
}

/**
 * The outward directions of the two legs from their shared vertex, and the
 * angle between them — the interior angle of the corner on the run's side.
 * Null when the segments do not form a corner.
 */
function cornerLegs(segA, segB) {
  if (!isCorner(segA, segB)) return null;
  const corner = sharedCornerPoint(segA, segB);
  if (!corner) return null;
  const dirA = unitFrom(
    corner,
    wallParamToSurfacePoint(segA.wall, segA.startParam)
  );
  const dirB = unitFrom(
    corner,
    wallParamToSurfacePoint(segB.wall, segB.endParam)
  );
  const d = Math.max(-1, Math.min(1, dot(dirA, dirB)));
  return { corner, dirA, dirB, angle: Math.acos(d) };
}

/**
 * True at a corner that is not square — the generator's rule
 * (`cornerDetection.SQUARE_CORNER_TOL_DEG`), so a corner the generator planned
 * as oblique is re-solved as oblique. A square corner gets the square L, the
 * blind box or the empty box; an oblique one stops its legs on the angle
 * bisector and fills the wedge with a kite.
 *
 * A bend through a DRAWN leg is square only at a true right angle
 * (`bendGeometry.isRightTurn`): its angle is the one the user drew, and the
 * generator's 5° — there for walls a scan finds a few degrees off square —
 * put a 90° L into an 87° bend, standing out from under the counter.
 */
function isObliqueCorner(segA, segB) {
  const legs = cornerLegs(segA, segB);
  if (!legs) return false;
  if (segA.wall.drawnFreeLeg || segB.wall.drawnFreeLeg) {
    const dA = segmentDirection(segA);
    const dB = segmentDirection(segB);
    return !(dA && dB && isRightTurn(turnDeg(dA, dB)));
  }
  return !isSquareCornerCos(dot(legs.dirA, legs.dirB));
}

/**
 * Where a leg of an oblique corner stops so the two runs' inner-room corners
 * just touch — the generator's `runStopDistance`: for a leg `depth` deep
 * meeting one `depthOther` deep. Equal depths (every run but a drawn island
 * leg set deeper than its run) give `depth / tan(θ/2)`, the bisector. Zero
 * for a joint too flat or too sharp to reserve against.
 */
function obliqueStop(segA, segB, depth, depthOther = depth) {
  const legs = cornerLegs(segA, segB);
  if (!legs) return 0;
  return runStopDistance(legs.angle, depth, depthOther);
}

/**
 * The kite that fills the wedge at an oblique corner: a polygon cabinet body
 * plus its countertop slab, from the generator's own builder, on legs
 * synthesized at the vertex and ordered so wall A's left normal points at
 * wall B (the builder winds the kite CCW from wall A). `stopA` / `stopB` are
 * the two legs' stops in metres.
 *
 * Wall B is synthesized ENDING at the corner (`wallBEnd: 'right'`), as the
 * generator passes its own (`materializeCandidate`): the builder reads leg B's
 * front off wall B's left normal, and a wall B STARTING at the corner has that
 * normal pointing away from wall A — out of the room. The builder only uses
 * it when the legs stop at different distances, so every symmetric kite was
 * right and every asymmetric one crossed itself with its fourth corner a
 * cabinet depth beyond the wall (104 corpus pieces, 2026-10-20).
 *
 * `slab` — `{ A: { front, back }, B: { front, back } }` in RUN order, metres
 * each leg's slab reaches in front of and behind its line — gives the slab
 * the legs' own reach (the builder's `slabFrontA` / `backA`): its front comes
 * to where the legs' fronts cross and its back meets an island leg's back
 * overhang. Absent, the slab is the body's outline (`bentRunFill.seamBend`
 * passes it once the legs' slabs exist).
 */
function buildObliqueKite({
  run,
  cornerIndex,
  stopA,
  stopB,
  floorY,
  ctx,
  slab = null,
}) {
  const segA = run.segments[cornerIndex];
  const segB = run.segments[cornerIndex + 1];
  const legs = cornerLegs(segA, segB);
  if (!legs || !(stopA >= 0) || !(stopB >= 0) || !(stopA + stopB > 0)) {
    return null;
  }
  if (!(stopA > 0) || !(stopB > 0)) {
    return zeroStopFiller({
      run,
      cornerIndex,
      legs,
      stopA,
      stopB,
      floorY,
      ctx,
    });
  }
  const { corner, dirA, dirB } = legs;
  const aFirst = dot(leftNormal(dirA), dirB) >= 0;
  const first = aFirst ? { seg: segA, dir: dirA } : { seg: segB, dir: dirB };
  const second = aFirst ? { seg: segB, dir: dirB } : { seg: segA, dir: dirA };
  // Each leg's own row depth (`segmentCornerDepth`) — one depth on every run
  // but a drawn island leg set deeper than the run it turns off.
  const depthOf = (seg) => segmentCornerDepth(seg, ctx);
  // The legs' slabs, run order (A = segment i), in the builder's order.
  const slabFirst = slab && (aFirst ? slab.A : slab.B);
  const slabSecond = slab && (aFirst ? slab.B : slab.A);
  const fill = buildCornerFillCountertop({
    corner: {
      wallA: legWall(first.seg.wall.id, corner, first.dir),
      wallB: legWallToCorner(second.seg.wall.id, corner, second.dir),
      wallBEnd: 'right',
      point: corner,
      stopA: aFirst ? stopA : stopB,
      stopB: aFirst ? stopB : stopA,
      depthA: depthOf(first.seg),
      depthB: depthOf(second.seg),
      ...(slab
        ? {
            backA: slabFirst.back,
            backB: slabSecond.back,
            slabFrontA: slabFirst.front,
            slabFrontB: slabSecond.front,
          }
        : {}),
    },
    floorY,
  });
  if (!fill) return null;
  for (const o of [fill.cabinet, fill.countertop]) {
    if (!o) continue;
    o.addedBy = 'manual-cabinet-run';
    o.runId = run.id;
    o.cornerIndex = cornerIndex;
  }
  return fill;
}

/**
 * A bend where one leg stops at the vertex — two legs of different depths at
 * an obtuse angle, where the deeper leg's front already clears the other's
 * there (`runStopDistance` is zero for it; found 2026-11-03 on a drawn
 * peninsula leg set to 36" after a 24" one). The generator's kite builder
 * takes no zero stop, so the filler here is the other leg's stretch from its
 * stop to the vertex, closed from its front corner to the deeper leg's front
 * corner — never into the deeper leg's first box, whose end face it meets.
 * Its counter comes from the run's bend step (`bentRunFill.bendCounterPatches`)
 * — only a drawn leg ever carries a depth of its own.
 *
 * @returns {{ cabinet: Object|null, countertop: null }}
 */
function zeroStopFiller({ run, cornerIndex, legs, stopA, stopB, floorY, ctx }) {
  const segA = run.segments[cornerIndex];
  const segB = run.segments[cornerIndex + 1];
  const nA = getWallAxes(segA.wall)?.wallNormal;
  const nB = getWallAxes(segB.wall)?.wallNormal;
  if (!nA || !nB) return null;
  const { corner: V, dirA, dirB } = legs;
  const at = (p, d, s) => ({ x: p.x + d.x * s, z: p.z + d.z * s });
  const dA = segmentCornerDepth(segA, ctx);
  const dB = segmentCornerDepth(segB, ctx);
  const polygon =
    stopA > 0
      ? [V, at(V, dirA, stopA), at(at(V, dirA, stopA), nA, dA), at(V, nB, dB)]
      : [V, at(V, nA, dA), at(at(V, dirB, stopB), nB, dB), at(V, dirB, stopB)];
  return {
    cabinet: buildPolygonFiller({
      id: `${run.id}_bend_${cornerIndex}_filler`,
      polygon,
      floorY,
      height: runBodyHeight(run),
      runId: run.id,
      cornerIndex,
    }),
    countertop: null,
  };
}

/** Which ends of segment i are interior corners shared with a neighbour. */
function segmentReservations(segments, i) {
  return {
    reserveStart: i > 0 && isCorner(segments[i - 1], segments[i]),
    reserveEnd:
      i < segments.length - 1 && isCorner(segments[i], segments[i + 1]),
  };
}

/**
 * The fillable param bounds [lo, hi] of segment i. Each corner end is trimmed
 * so cabinets/bays/appliances stay clear of the corner cabinet. `cornerTrims`
 * (per-segment { start, end } amounts, from the blind-corner plan) overrides
 * the default 36" footprint trim — e.g. a blind corner's extending leg gets a
 * 0 trim (runs to the vertex) while the other leg gets a 24" depth trim.
 */
function segmentFillBounds(segments, i, cornerTrims = null) {
  const seg = segments[i];
  let s = seg.startParam;
  let e = seg.endParam;
  const dir = Math.sign(e - s) || 1;
  if (cornerTrims) {
    s += dir * (cornerTrims[i] ? cornerTrims[i].start : 0);
    e -= dir * (cornerTrims[i] ? cornerTrims[i].end : 0);
  } else {
    const { reserveStart, reserveEnd } = segmentReservations(segments, i);
    if (reserveStart) s += dir * CORNER_CABINET_FOOTPRINT;
    if (reserveEnd) e -= dir * CORNER_CABINET_FOOTPRINT;
  }
  // On a short segment the two corner reservations can cross over — return a
  // zero-width (empty) span so callers skip it.
  if ((e - s) * dir <= 0) {
    const mid = (s + e) / 2;
    return { lo: mid, hi: mid };
  }
  return { lo: Math.min(s, e), hi: Math.max(s, e) };
}

const leftNormal = (d) => ({ x: -d.z, z: d.x });
const dot = (a, b) => a.x * b.x + a.z * b.z;
function unitFrom(from, to) {
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  const len = Math.hypot(dx, dz) || 1;
  return { x: dx / len, z: dz / len };
}

/** A leg-wall anchored at the corner, oriented outward along `dir`. */
function legWall(realWallId, corner, dir) {
  return {
    id: realWallId,
    alignment: {
      bottomLeft: { x: corner.x, z: corner.z },
      bottomRight: { x: corner.x + dir.x, z: corner.z + dir.z },
    },
  };
}

/** The same leg, oriented INTO the corner: it ends there (`wallBEnd: 'right'`). */
function legWallToCorner(realWallId, corner, dir) {
  return {
    id: realWallId,
    alignment: {
      bottomLeft: { x: corner.x + dir.x, z: corner.z + dir.z },
      bottomRight: { x: corner.x, z: corner.z },
    },
  };
}

/**
 * Invoke `cb({ cornerIndex, corner, wallA, wallB })` for each interior vertex of
 * the run that should carry a corner cabinet — `wallA`/`wallB` are the ordered
 * leg-walls (wallA's left normal points at wallB, so the L folds into the
 * interior quadrant). Degenerate corners are skipped. Shared by the lower
 * corner-unit and upper corner-cabinet builders so both place the L identically.
 */
function eachCabinetCorner(run, cabinetCorners, cb) {
  const { segments } = run;
  for (let i = 0; i < segments.length - 1; i += 1) {
    if (cabinetCorners && !cabinetCorners.has(i)) continue;
    const segA = segments[i];
    const segB = segments[i + 1];
    const corner = isCorner(segA, segB) ? sharedCornerPoint(segA, segB) : null;
    if (!corner) continue;

    // Outward direction along each leg's cabinet run, from the corner.
    const dirA = unitFrom(
      corner,
      wallParamToSurfacePoint(segA.wall, segA.startParam)
    );
    const dirB = unitFrom(
      corner,
      wallParamToSurfacePoint(segB.wall, segB.endParam)
    );

    const DEGENERATE_EPS = 1e-6;
    if (
      Math.hypot(dirA.x, dirA.z) < DEGENERATE_EPS ||
      Math.hypot(dirB.x, dirB.z) < DEGENERATE_EPS
    ) {
      continue;
    }

    const legA = legWall(segA.wall.id, corner, dirA);
    const legB = legWall(segB.wall.id, corner, dirB);

    // buildCornerCabinet folds the box toward wallA's LEFT normal. Choose
    // the ordering whose left normal points at the other leg so the box
    // lands in the interior quadrant between the two legs.
    const swapped = dot(leftNormal(dirA), dirB) < 0;
    const ordered = swapped
      ? { wallA: legB, wallB: legA }
      : { wallA: legA, wallB: legB };

    // Both legs in their OWN segment's terms, so a caller can test the
    // corner unit's footprint against that segment's wall constraints
    // (`ordered` may have swapped A/B to fold the box the right way).
    const legs = [
      { segIndex: i, wall: segA.wall, dir: dirA },
      { segIndex: i + 1, wall: segB.wall, dir: dirB },
    ];
    cb({
      cornerIndex: i,
      corner,
      wallA: ordered.wallA,
      wallB: ordered.wallB,
      // `wallA` is segment i+1's leg, not segment i's. A caller sizing the two
      // legs DIFFERENTLY (an upper L on shrunk legs) has to know.
      swapped,
      legs,
    });
  }
}

/**
 * The L's leg at corner `i` (metres): the size `cabinetCorners` — a Map from
 * the fill — carries for it, else the standard 36" footprint.
 */
function cornerLegOf(cabinetCorners, i) {
  const leg = cabinetCorners?.get?.(i);
  return leg > 0 ? leg : CORNER_CABINET_FOOTPRINT;
}

/**
 * One L-corner cabinet per interior vertex of the run. When `cabinetCorners` is
 * given, only those corner indices get a cabinet (others are blind/empty), at
 * the leg each carries (`cornerLegOf`: a line's 33" L is built at 33").
 *
 * Built at the RUN's own depth (`cornerBoxFor`), not the engine's standard
 * 24" — this used to pass no opts at all, so the L stood 24" deep between
 * straight bays built at `ctx.cabinetDepth`.
 */
function buildCornerUnits(run, floorY, cabinetCorners = null, ctx = null) {
  const units = [];
  const cornerBox = cornerBoxFor(ctx);
  // The run's dimension profile sets the corner box's HEIGHT (a 32" vanity
  // corner on a vanity run). For the kitchen profile this passes the same
  // constant the builder would default to, so nothing changes. `cornerBox` is
  // the run's DEPTH footprint and stays independent of it.
  const profile = profileOf(run);
  eachCabinetCorner(
    run,
    cabinetCorners,
    ({ cornerIndex, corner, wallA, wallB }) => {
      const leg = cornerLegOf(cabinetCorners, cornerIndex);
      const cab = buildCornerCabinet({
        wallA,
        wallB,
        cornerPoint: corner,
        floorY,
        opts: {
          cornerBox,
          height: runBodyHeight(run),
          alongA: leg,
          alongB: leg,
        },
      });
      if (!cab) return;
      cab.addedBy = 'manual-cabinet-run';
      cab.runId = run.id;
      // The corner's segment-pair index (between segment i and i+1). A
      // per-corner appearance override targets this index so Unit-scope
      // cosmetics reach the corner cabinet (it isn't an auto bay).
      cab.cornerIndex = cornerIndex;
      units.push(cab);
    }
  );
  return units;
}

/**
 * A single-sided (blind) corner unit on the EXTENDING leg of a blind corner:
 * the generator's `buildSingleSidedCornerCabinet` seated at the run's vertex
 * with its body `length` along the leg. The other leg stops a cabinet-depth
 * short and butts against this body, so the box in the corner is blind and
 * the unit's door sits on its accessible portion — which is what a plain
 * door cabinet packed into that box could never be.
 *
 * Built on a synthetic wall covering exactly the unit's span, ordered the
 * real wall's way so the unit faces the room; `range` comes back in the real
 * wall's params. `end` is which end of `segment` is the vertex, and
 * `cornerWallId` is the neighbouring segment's wall — the unit's end stands
 * against it, so it names that wall too.
 */
function buildSingleSidedCornerUnit({
  segment,
  end,
  length,
  depth,
  floorY,
  cornerIndex,
  runId,
  cornerWallId = null,
}) {
  const { wall } = segment;
  const vertex = end === 'end' ? segment.endParam : segment.startParam;
  const other = end === 'end' ? segment.startParam : segment.endParam;
  const away = Math.sign(other - vertex) || 1; // +1: the leg runs to higher params
  const p0 = Math.min(vertex, vertex + away * length);
  const p1 = Math.max(vertex, vertex + away * length);
  const a = wallParamToSurfacePoint(wall, p0);
  const b = wallParamToSurfacePoint(wall, p1);
  if (!a || !b) return null;
  const cab = buildSingleSidedCornerCabinet({
    // The wall on the other side of the vertex — `isCorner` has already
    // established that both segments have real walls and that they differ,
    // so this is always a second real wall rather than a virtual leg.
    cornerWallId,
    wall: {
      id: wall.id,
      alignment: {
        bottomLeft: { x: a.x, z: a.z },
        bottomRight: { x: b.x, z: b.z },
      },
    },
    cornerEnd: away > 0 ? 'left' : 'right',
    floorY,
    length,
    ...(depth != null ? { depth } : {}),
  });
  if (!cab) return null;
  cab.range = { start: p0, end: p1 };
  cab.cornerIndex = cornerIndex;
  cab.runId = runId;
  cab.addedBy = 'manual-cabinet-run';
  return cab;
}

/**
 * The upper L at each corner the UPPER tier decided to hang one at.
 *
 * Which corners, and on what legs, is the upper tier's call
 * (`upperTier.planUpperCorners` → `upperCornerLegs`): it owns the upper band's
 * keep-outs, and the legs' own trims have to agree with the unit's reach.
 * This only builds. Same placement as `buildCornerUnits`, at upper
 * height / depth (12" deep, 30" tall, hung above the counter).
 *
 * @param {{bottomY: number, height: number}} band the run's upper band
 *   (`upperBand.upperBandFor`) — the L hangs in the same slot as the straight
 *   uppers beside it
 * @param {Map<number, {reachA: number, reachB: number}>} upperLs by corner
 *   index: how far the L reaches along segment i's leg (`reachA`) and along
 *   segment i+1's (`reachB`), in metres from the vertex
 * @param {{ stack?: boolean }} [opts] `stack` — the L of a STACKED row
 *   (`upperStack.js`): it stands on `band.bottomY` (a vendor snap lowers its
 *   top, never raises its bottom), and its id and group carry `_stack` so it
 *   can never share the id of the L it stands on
 */
function buildCornerUppers(run, floorY, upperLs, band, opts = {}) {
  const stack = opts.stack === true;
  const units = [];
  // A vendor family hangs its corner L at a height it makes (design
  // 2026-10-05, D3) — the tallest that fits the band, top on the line — or
  // not at all. Required lazily: families/vendorSizes load the corner sets.
  const { familyOf } = require('./families');
  const { snapUpperHeightIn } = require('./vendorSizes');
  // a stacked L is a height the family OFFERS, in every family
  const { offeredUpperHeightIn } = require('./upperHeights');
  const snap = stack ? offeredUpperHeightIn : snapUpperHeightIn;
  const family = familyOf(run);
  eachCabinetCorner(
    run,
    new Set(upperLs.keys()),
    ({ cornerIndex, corner, wallA, wallB, swapped }) => {
      const { reachA, reachB } = upperLs.get(cornerIndex);
      let { height, bottomY } = band;
      const snapped = snap(
        family,
        'wall-pie',
        Math.max(reachA, reachB) / 0.0254,
        band.height / 0.0254
      );
      if (snapped === null) return; // the vendor makes no corner that fits
      if (snapped !== undefined) {
        height = snapped * 0.0254;
        if (!stack) bottomY = band.topY - height;
      }
      const cab = buildCornerCabinet({
        wallA,
        wallB,
        cornerPoint: corner,
        floorY,
        opts: {
          isUpper: true,
          height,
          cornerBox: UPPER_CORNER_BOX,
          alongA: swapped ? reachB : reachA,
          alongB: swapped ? reachA : reachB,
          bottomY,
          label: 'Corner Upper Cabinet',
          idPrefix: stack
            ? 'corner_upper_cabinet_stack'
            : 'corner_upper_cabinet',
          fixtureGroupPrefix: stack
            ? 'kitchenCornerUpperCabinet_stack'
            : 'kitchenCornerUpperCabinet',
        },
      });
      if (!cab) return;
      if (stack) cab.category = { ...cab.category, stack: true };
      cab.addedBy = 'manual-cabinet-run';
      cab.runId = run.id;
      cab.cornerIndex = cornerIndex;
      units.push(cab);
    }
  );
  return units;
}

/**
 * Counter items covering each corner's 36" footprint on BOTH legs, so the leg
 * countertops extend over the corner cabinet (the two leg slabs overlap in the
 * corner box). Without these the corner cabinet has no countertop and both leg
 * slabs stop short of the corner. The synthetic ids just give the run a parent
 * so a countertop is still emitted when the legs are too short for cabinets.
 */
function buildCornerCounterItems(
  run,
  cabinetCorners = null,
  cornerUnits = [],
  ctx = null
) {
  const { segments } = run;
  const items = [];
  // Slab depth follows the run, like every other counter item (a straight bay
  // and the single-sided unit both pass their own `depth`). Omitting it left
  // the corner slab at the default 24" while its leg slabs sat at the run's
  // depth, so a shallow run stepped at the corner in the countertop too.
  const depth = cornerBoxFor(ctx);
  for (let i = 0; i < segments.length - 1; i += 1) {
    if (cabinetCorners && !cabinetCorners.has(i)) continue; // blind/empty: leg counters cover it
    const segA = segments[i];
    const segB = segments[i + 1];
    if (!isCorner(segA, segB)) continue;

    // The corner countertop rests on the corner cabinet, so reference its real
    // id — the leg countertops' `parents` then resolve to a materialized object
    // instead of a placeholder. Degenerate corner with no cabinet → a synthetic
    // id that still extends the countertop (coverage extent only).
    const cornerUnit = cornerUnits.find((u) => u.cornerIndex === i);
    const parentId = cornerUnit ? cornerUnit.id : `cornerFill_${i}`;

    const FOOT = cornerLegOf(cabinetCorners, i);
    const dirA = Math.sign(segA.endParam - segA.startParam) || 1;
    const aInner = segA.endParam - dirA * FOOT; // the L's leg into the run from the corner
    const dirB = Math.sign(segB.endParam - segB.startParam) || 1;
    const bInner = segB.startParam + dirB * FOOT;

    items.push({
      wallId: segA.wall.id,
      range: {
        start: Math.min(segA.endParam, aInner),
        end: Math.max(segA.endParam, aInner),
      },
      type: 'lower-cabinet',
      id: parentId,
      depth,
    });
    // Leg A owns the corner box: its slab runs the L's full leg into the corner.
    // Leg B's therefore STARTS one box in, where leg A's ends. Both used to run
    // the full 36", so the two slabs covered the same square — two coplanar
    // countertops z-fighting in 3D, and `collectObjectFinishes` bills
    // `dims.x * dims.z` per slab, so ~4 ft² was double-counted at every corner.
    // The generator has always trimmed its wall-B item for exactly this reason.
    const bOuter = segB.startParam + dirB * depth;
    const bRange = {
      start: Math.min(bOuter, bInner),
      end: Math.max(bOuter, bInner),
    };
    if (bRange.end - bRange.start > 1e-6) {
      items.push({
        wallId: segB.wall.id,
        range: bRange,
        type: 'lower-cabinet',
        id: parentId,
        depth,
      });
    }
  }
  return items;
}

/**
 * Recover the cornerIndex of an already-built corner cabinet from its
 * parentWallIds (with center as a tiebreak when a wall pair recurs). Heals
 * corner cabinets materialized before cornerIndex tagging existed — they live in
 * saved room.objects and aren't re-solved on load. `run.segments` must be
 * rehydrated (each segment carries a `.wall`). Returns -1 if nothing matches.
 */
// One reader for a corner unit's leg walls — see cornerUnit.js (#1527).
const { cornerUnitWallIds } = require('./cornerUnit');

function cornerIndexForUnit(run, unit) {
  const ids = cornerUnitWallIds(unit);
  if (ids.length < 2) return -1;
  const want = new Set(ids);
  const center = unit?.center;
  const segments = run?.segments || [];
  let best = -1;
  let bestD = Infinity;
  for (let i = 0; i < segments.length - 1; i += 1) {
    const segA = segments[i];
    const segB = segments[i + 1];
    const cp = sharedCornerPoint(segA, segB);
    if (!cp) continue;
    if (!want.has(segA.wall.id) || !want.has(segB.wall.id)) continue;
    if (!center) return i;
    const d = (cp.x - center.x) ** 2 + (cp.z - center.z) ** 2;
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

module.exports = {
  cornerUnitWallIds,
  isCorner,
  isObliqueCorner,
  cornerLegs,
  obliqueStop,
  buildObliqueKite,
  segmentReservations,
  segmentFillBounds,
  buildCornerUnits,
  buildCornerUppers,
  // each corner's point and legs, for a builder that covers the L its own
  // way (`upperStack`'s straight boxes over a corner)
  eachCabinetCorner,
  buildCornerCounterItems,
  buildSingleSidedCornerUnit,
  cornerIndexForUnit,
  CORNER_CABINET_FOOTPRINT,
};
